import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAdminAuth } from '@/hooks/useAdminAuth';


interface AdminRouteProps {
  children: ReactNode;
}

export function AdminRoute({ children }: AdminRouteProps) {
  const { user, canAccessAdmin, isLoading } = useAdminAuth();
  const location = useLocation();

  // Auth resolved: not logged in → redirect
  if (!isLoading && !user) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  // Auth resolved: logged in but no admin/moderator role
  if (!isLoading && user && !canAccessAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4 max-w-md mx-auto p-8">
          <div className="w-16 h-16 mx-auto rounded-full bg-destructive/10 flex items-center justify-center">
            <span className="text-3xl">🚫</span>
          </div>
          <h1 className="text-2xl font-bold">Access Denied</h1>
          <p className="text-muted-foreground">
            You don't have permission to access the admin area. 
            Please contact an administrator if you believe this is an error.
          </p>
          <a 
            href="/" 
            className="inline-block mt-4 text-primary hover:underline"
          >
            Return to Home
          </a>
        </div>
      </div>
    );
  }

  // Either auth is still loading OR user is verified — render children immediately.
  // The page itself renders (AdminLayout paints header right away).
  // While loading, we overlay a tiny inline spinner inside the content area.
  return (
    <>
      {children}
      {isLoading && (
        <div className="fixed inset-0 z-40 bg-background/60 backdrop-blur-sm pointer-events-none" />
      )}
    </>
  );
}

export default AdminRoute;
